<template>
    <div class="main-layout__container">
        <div class="content__header content__header--one-item-per-line">
            <div class="content-header__back">
                <UIBackButton
                    :text="'В мой профиль'"
                    :to="'/profile'"
                />
            </div>

            <div class="content-header__title">Редактирование реквизитов</div>

            <div class="content-header__background content-header__background--add-edit-page"></div>
        </div>

        <UILoader v-if="isDealerLoading" class="editable" />

        <template v-else>
            <div class="content__body">
                <div class="form__content">
                    <div class="form__fields">
                        <div class="form__subtitle">Юридические данные</div>

                        <UIInput
                            id="legalName"
                            v-model="form.name"
                            label="Название юр.лица*"
                            :error-message="errors.name?.join() ?? ''"
                        />

                        <div class="form__field-group">
                            <UIInput
                                id="inn"
                                v-model="form.inn"
                                label="ИНН*"
                                only-numbers
                                :error-message="errors.inn?.join() ?? ''"
                            />

                            <UIInput
                                id="kpp"
                                v-model="form.kpp"
                                label="КПП*"
                                only-numbers
                                :error-message="errors.kpp?.join() ?? ''"
                            />

                            <UIInput
                                id="ogrn"
                                v-model="form.ogrn"
                                label="ОГРН*"
                                only-numbers
                                :error-message="errors.ogrn?.join() ?? ''"
                            />
                        </div>

                        <UIInput
                            id="signPosition"
                            v-model="form.signer_position"
                            label="Должность подписанта*"
                            :error-message="errors.signer_position?.join() ?? ''"
                        />

                        <UIInput
                            id="signName"
                            v-model="form.signer_fio"
                            label="ФИО подписанта*"
                            :error-message="errors.signer_fio?.join() ?? ''"
                        />

                        <UIInput
                            id="rpDetails"
                            v-model="form.signatory_position_and_name"
                            label="Должность и ФИО подписанта (в РП)*"
                            :error-message="errors.signatory_position_and_name?.join() ?? ''"
                        />

                        <div class="form__field-group">
                            <UIInput
                                id="basedOn"
                                v-model="form.signer_basis"
                                label="На основании (в РП)*"
                                :error-message="errors.signer_basis?.join() ?? ''"
                            />

                            <UIInput
                                id="contractNumber"
                                v-model="form.contract_number"
                                label="Номер договора*"
                                :error-message="errors.contract_number?.join() ?? ''"
                            />

                            <UIDatePicker
                                id="contractDate"
                                v-model="form.contract_date"
                                label="Дата договора*"
                                :options="{ enableTime: false, dateFormat: 'd.m.Y' }"
                                :error-message="errors.contract_date?.join() ?? ''"
                            />
                        </div>

                        <!--<UIInput
                            id="basedOn"
                            v-model="form.signer_basis_2"
                            label="Фактический адрес дилера"
                            :error-message="errors.signer_basis?.join() ?? ''"
                        />-->

                        <UIInput
                            id="legalAddress"
                            v-model="form.address"
                            label="Юридический адрес*"
                            :error-message="errors.address?.join() ?? ''"
                        />
                    </div>

                    <div class="form__fields">
                        <div class="form__subtitle">Банковские реквизиты</div>

                        <div class="form__field-group">
                            <UIInput
                                id="account"
                                v-model="form.bank_account"
                                label="Расчетный счет*"
                                only-numbers
                                :error-message="errors.bank_account?.join() ?? ''"
                            />

                            <UIInput
                                id="corrAccount"
                                v-model="form.correspondent_account"
                                label="Корр. Счет*"
                                only-numbers
                                :error-message="errors.correspondent_account?.join() ?? ''"
                            />

                            <UIInput
                                id="bic"
                                v-model="form.bank_identification_code"
                                label="БИК*"
                                only-numbers
                                :error-message="errors.bank_identification_code?.join() ?? ''"
                            />
                        </div>

                        <UIInput
                            id="bankName"
                            v-model="form.bank_name"
                            label="Название банка*"
                            :error-message="errors.bank_name?.join() ?? ''"
                        />
                    </div>
                </div>
            </div>

            <div class="content__footer">
                <div class="form__actions">
                    <UISimpleButton
                        button-type="outlined"
                        @clicked="goToProfilePage"
                    >
                        Выйти без сохранения
                    </UISimpleButton>

                    <UISimpleButton
                        :loading="saveLoading"
                        @clicked="handleSave"
                    >
                        Сохранить
                    </UISimpleButton>
                </div>
            </div>
        </template>
    </div>
</template>

<script lang="ts" setup>
    import { onMounted, ref } from 'vue';
    import { useRouter } from 'vue-router';

    import { Dealer } from '@/types/dealer.type.ts';
    import { LegalEntityUpdateForm, LegalEntityUpdateFormErrors } from '@/types/legal-entity.type.ts';

    import useDealerRepository from '@/repositories/dealer/useDealerRepository.ts';
    import useNotifications from '@/utils/useNotifications.ts';
    import useUserRepository from '@/repositories/user/useUserRepository.ts';

    import { formatFromUTC, formatToUTC } from '@/utils/DateTimeFormatter.ts';

    import UIBackButton from '@/components/ui/UIBackButton/UIBackButton.vue';
    import UIDatePicker from '@/components/ui/UIDatePicker/UIDatePicker.vue';
    import UIInput from '@/components/ui/UIInput/UIInput.vue';
    import UILoader from '@/components/ui/UILoader/UILoader.vue';
    import UISimpleButton from '@/components/ui/UISimpleButton/UISimpleButton.vue';

    const router = useRouter();
    const { isDealerLoading, getDealer, updateDealerLegalEntity } = useDealerRepository();
    const { getCurrentUser } = useUserRepository();
    const { showSuccess } = useNotifications();

    const form = ref<LegalEntityUpdateForm>({
        name: '',
        inn: '',
        kpp: '',
        ogrn: '',
        signer_position: '',
        signer_fio: '',
        signatory_position_and_name: '',
        signer_basis: '',
        address: '',
        bank_account: '',
        correspondent_account: '',
        bank_identification_code: '',
        bank_name: '',
        contract_number: '',
        contract_date: '',
    });
    const saveLoading = ref<boolean>(false);
    const errors = ref<LegalEntityUpdateFormErrors>({});

    function handleSave(): void {
        saveLoading.value = true;
        errors.value = {};

        const userCurrentDealerId = getCurrentUser().dealer_id;

        if (!userCurrentDealerId) {
            return;
        }

        updateDealerLegalEntity(userCurrentDealerId, mapUpdatedLegalEntityFromForm())
            .then(() => {
                showSuccess({
                    title: 'Изменения в реквизитах дилера сохранены',
                });

                goToProfilePage();
            })
            .catch((updateErrors: LegalEntityUpdateFormErrors) => {
                errors.value = updateErrors;
            })
            .finally(() => {
                saveLoading.value = false;
            });
    }

    function mapUpdatedLegalEntityFromForm(): LegalEntityUpdateForm {
        const contractDate = form.value.contract_date !== undefined && form.value.contract_date.length > 0 ? formatToUTC(form.value.contract_date, {
            fromFormat: 'dd.LL.yyyy',
            toFormat: 'yyyy-LL-dd',
            withTime: false,
        }): '';

        return { ...form.value, contract_date: contractDate };
    }

    function goToProfilePage(): void {
        router.push({
            name: 'Profile',
            params: {
                tab: 'requisites',
            },
        });
    }

    function fetchDealer(): void {
        const userCurrentDealerId = getCurrentUser().dealer_id;

        if (!userCurrentDealerId) {
            return;
        }

        getDealer(userCurrentDealerId).then((dealer: Dealer) => {
            form.value = {
                name: dealer.legal_entity?.name ?? '',
                inn: dealer.legal_entity?.inn ?? '',
                kpp: dealer.legal_entity?.kpp ?? '',
                ogrn: dealer.legal_entity?.ogrn ?? '',
                signer_position: dealer.legal_entity?.signer_position ?? '',
                signer_fio: dealer.legal_entity?.signer_fio ?? '',
                signatory_position_and_name: dealer.legal_entity?.signatory_position_and_name ?? '',
                signer_basis: dealer.legal_entity?.signer_basis ?? '',
                address: dealer.legal_entity?.address ?? '',
                bank_account: dealer.legal_entity?.bank_account ?? '',
                correspondent_account: dealer.legal_entity?.correspondent_account ?? '',
                bank_identification_code: dealer.legal_entity?.bank_identification_code ?? '',
                bank_name: dealer.legal_entity?.bank_name ?? '',
                contract_number: dealer.legal_entity?.contract_number,
                contract_date: dealer.legal_entity?.contract_date
                    ? formatFromUTC(dealer.legal_entity?.contract_date, { toFormat: 'dd.LL.yyyy' })
                    : '',
            };
        });
    }

    onMounted(() => {
        fetchDealer();
    });
</script>
